import { useState } from 'react';

import { onFixtures } from '../queries/source';
import { ageFrom, dateTime } from '../utils/format';

// The small pieces every coordination screen repeats.

// WHERE WHAT IS ON SCREEN CAME FROM. Printed on every screen, because the one
// thing this demo must never do is show a number nobody can trace to a route.
export function Origin({ origin }) {
    if (!origin) return null;
    return (
        <details className="coord__origin">
            <summary>Fuente de los datos</summary>
            <code>{origin.request}</code>
            {origin.kind === 'fixture' && (
                <span className="coord__origin-file">
                    · muestra «{origin.sample}» de <code>{origin.file}</code>
                </span>
            )}
        </details>
    );
}

// A scope the fixtures do not answer for. It is not an error and it is not an
// empty screen: it says which sample exists and what the real route would be.
export function Unavailable({ children }) {
    return (
        <div className="coord__unavailable">
            <strong>Este demo no tiene esa muestra.</strong>
            <p>{children}</p>
        </div>
    );
}

// «no se sabe», with the reason the contract gave. NEVER a zero and never an
// empty cell: those two read as "measured, and it was nothing".
export function Unknown({ reason, children }) {
    return (
        <span className="coord__unknown" title={reason ? `razón: ${reason}` : undefined}>
            {children ?? 'no se sabe'}
        </span>
    );
}

// A moment, with how old it was WHEN THE ANSWER WAS TAKEN — not against this
// browser's clock, which is not the day the fixture was captured.
export function Moment({ at, observedAt, empty = 'sin marca' }) {
    if (!at) return <Unknown>{empty}</Unknown>;
    const age = ageFrom(observedAt, at);
    return (
        <span className="coord__moment" title={at}>
            {dateTime(at)}
            {age && <span className="coord__moment-age">{age}</span>}
        </span>
    );
}

export function Facts({ children }) {
    return <dl className="coord__facts">{children}</dl>;
}

export function Fact({ label, children, wide }) {
    return (
        <div className={`coord__fact${wide ? ' coord__fact--wide' : ''}`}>
            <dt>{label}</dt>
            <dd>{children}</dd>
        </div>
    );
}

// The separations the contract insists on: two things that are not the same
// thing, with the sentence that says why. They travel in the answer; the demo
// shows them instead of paraphrasing.
export function Separations({ items }) {
    const list = Array.isArray(items)
        ? items
        : Object.entries(items ?? {}).map(([id, means]) => ({ id, means }));

    if (!list.length) return null;

    return (
        <details className="coord__separations">
            <summary>Cómo interpretar estos estados</summary>
            <ul>
                {list.map(item => (
                    <li key={item.id}>
                        {item.left && item.right && (
                            <code className="coord__separations-pair">
                                {item.left} ≠ {item.right}
                            </code>
                        )}
                        <span>{item.means}</span>
                    </li>
                ))}
            </ul>
        </details>
    );
}

// The answer as it arrived. A demo is read by people who are deciding what the
// screen should show: the field they are missing is easier to name with the
// object in front of them. It is laundered prose already (`[secreto]`, paths
// cut, 240 characters) and the demo does not undo any of that.
export function RawJson({ value, label = 'Ver la respuesta como llegó' }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="coord__raw">
            <button
                type="button"
                className="coord__raw-toggle"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
                {open ? 'Ocultar la respuesta' : label}
            </button>
            {open && <pre className="coord__raw-body">{JSON.stringify(value, null, 2)}</pre>}
        </div>
    );
}

// The banner that must not be possible to miss: this is a demo, over fixtures,
// and the final screen is not decided.
export function DemoBanner() {
    if (!onFixtures) return null;
    return (
        <p className="coord__banner">
            <strong>Vista de ejemplo.</strong> Los datos son de prueba. Los controles no envían
            órdenes.
        </p>
    );
}

export function RefreshStatus({ query }) {
    return (
        <div className="coord__refresh">
            <span role="status">
                {query.error
                    ? 'No se pudo actualizar; los datos pueden estar desactualizados.'
                    : query.isFetching
                      ? 'Actualizando…'
                      : query.dataUpdatedAt
                        ? 'Actualizado a las ' +
                          new Date(query.dataUpdatedAt).toLocaleTimeString('es')
                        : 'Esperando datos'}
                {!onFixtures && !query.error && ' · cada 15 s'}
            </span>
            <button
                type="button"
                className="coord__button"
                onClick={() => query.refetch()}
                disabled={query.isFetching}
            >
                Actualizar
            </button>
        </div>
    );
}

export function PageControls({ total, offset, limit, onChange, busy }) {
    if (!total) return null;
    return (
        <nav className="coord__pagination coord__page-controls" aria-label="Paginación">
            <span>
                {offset + 1}–{Math.min(offset + limit, total)} de {total}
            </span>
            <button
                type="button"
                className="coord__button"
                disabled={busy || offset === 0}
                onClick={() => onChange(Math.max(0, offset - limit))}
            >
                Anterior
            </button>
            <button
                type="button"
                className="coord__button"
                disabled={busy || offset + limit >= total || onFixtures}
                onClick={() => onChange(offset + limit)}
            >
                Siguiente
            </button>
        </nav>
    );
}

export function TechnicalDetails({ children, label = 'Detalles técnicos' }) {
    return (
        <details className="coord__technical">
            <summary>{label}</summary>
            {children}
        </details>
    );
}
